import React, { useState, useEffect } from 'react';
import { X, RefreshCw, Download, Check } from 'lucide-react';
import { APP_BUILD } from '../../appVersion';
import {
  forzarActualizacionCompleta,
  sePuedeInstalar,
  alCambiarInstalable,
  instalarApp,
  estaInstalada,
} from '../../utils/pwa';

/**
 * Aviso de versión nueva y actualización forzada.
 *
 * Aparece solo cuando hay una versión publicada más nueva que la que está
 * corriendo, y también se puede abrir a mano desde el menú de usuario para
 * cuando algo se ve raro y uno sospecha de la caché.
 *
 * El botón hace lo que en escritorio sería Ctrl+Shift+R —imposible en un
 * teléfono—: borra el service worker, las cachés y vuelve a pedir todos los
 * archivos al servidor.
 */

const fechaLegible = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

export const ActualizacionApp = ({ versionNueva, onCerrar }) => {
  const [actualizando, setActualizando] = useState(false);
  const [instalable, setInstalable] = useState(sePuedeInstalar());
  const [instalada] = useState(estaInstalada());

  useEffect(() => alCambiarInstalable(setInstalable), []);

  // Escape cierra, como cualquier diálogo.
  useEffect(() => {
    const alPulsar = (e) => { if (e.key === 'Escape' && !actualizando) onCerrar?.(); };
    document.addEventListener('keydown', alPulsar);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = overflowPrevio;
    };
  }, [onCerrar, actualizando]);

  const actualizar = async () => {
    setActualizando(true);
    await forzarActualizacionCompleta();
    // No hace falta reponer el estado: la línea anterior recarga la página.
  };

  const hayNueva = Boolean(versionNueva);

  return (
    <div
      id="actualizacion-app-overlay"
      className="modal-overlay dialogo dialogo--actualizar"
      onClick={() => !actualizando && onCerrar?.()}
    >
      <div
        id="actualizacion-app"
        className="modal-caja dialogo__caja"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="actualizacion-app-titulo"
      >
        {!actualizando && (
          <button
            id="actualizacion-app-cerrar"
            onClick={() => onCerrar?.()}
            aria-label="Cerrar"
            className="tap-exempt dialogo__cerrar"
          >
            <X size={20} />
          </button>
        )}

        {/* El color del recuadro depende de si hay versión nueva: es un dato,
            no una decisión de diseño fija. */}
        <div className="dialogo__icono" style={{ background: hayNueva ? '#fff1eb' : '#f3f4f6' }}>
          <RefreshCw size={21} color={hayNueva ? '#FF4500' : '#6b7280'} />
        </div>

        <h2
          id="actualizacion-app-titulo"
          className="dialogo__titulo"
        >
          {hayNueva ? 'Hay una versión nueva' : 'Actualizar la aplicación'}
        </h2>

        <p className="dialogo__texto">
          {hayNueva
            ? `Se publicó la versión ${versionNueva.version} y estás usando la ${APP_BUILD.version}. Actualizá para ver los últimos cambios.`
            : 'Si notás que algo no refleja los últimos cambios —sobre todo en el teléfono, donde no se puede refrescar a fondo— usá este botón para volver a descargar la aplicación entera.'}
        </p>

        {/* Versión en uso: lo primero que hace falta saber cuando algo se ve raro. */}
        <div
          id="actualizacion-app-version"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, padding: '11px 13px', border: '1px solid #e5e7eb',
            borderRadius: 10, marginBottom: 16,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#111' }}>
              Versión {APP_BUILD.version}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
              {fechaLegible(APP_BUILD.builtAt) || 'sin fecha de publicación'}
            </div>
          </div>
          {hayNueva && (
            <span className="insignia insignia--naranja">
              v{versionNueva.version} disponible
            </span>
          )}
        </div>

        <div className="dialogo__acciones">
          <button
            id="actualizacion-app-actualizar"
            onClick={actualizar}
            disabled={actualizando}
            className="boton-dialogo boton-dialogo--principal boton-dialogo--crece"
          >
            <RefreshCw size={15} className={actualizando ? 'spin' : undefined} />
            {actualizando ? 'Actualizando…' : 'Actualizar ahora'}
          </button>

          {/* Instalar: sólo cuando el navegador dice que se puede. En iOS no
              existe este evento, y si ya está instalada tampoco tiene sentido. */}
          {!instalada && instalable && (
            <button
              id="actualizacion-app-instalar"
              onClick={instalarApp}
              disabled={actualizando}
              className="boton-dialogo boton-dialogo--secundario boton-dialogo--crece"
            >
              <Download size={15} />
              Instalar app
            </button>
          )}
        </div>

        {instalada && (
          <p style={{
            display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
            fontSize: '0.78rem', color: '#16a34a', margin: '12px 0 0',
          }}>
            <Check size={13} /> Estás usando la app instalada
          </p>
        )}

        {actualizando && (
          <p style={{ fontSize: '0.78rem', color: '#9ca3af', textAlign: 'center', margin: '12px 0 0' }}>
            No cierres la aplicación. Se va a recargar sola en unos segundos.
          </p>
        )}
      </div>
    </div>
  );
};
